import { ApisauceInstance, create } from "apisauce";
import { ApiConfig, DEFAULT_API_CONFIG } from "../config/api.config";

// utils
import * as localStorageUtil from "../utils/local-storage.utils";
import { isEmpty } from "../utils/global.utils";

//constants
import * as localStoageKeys from "../constants/local-storage.constants";

// import { logout } from "../Router";
// import { toast } from "react-toastify";
class Api {
  /**
   * The underlying apisauce instance which performs the requests.
   */
  apisauce: ApisauceInstance;

  /**
   * Configurable options.
   */
  config: ApiConfig;

  /**
   * Param for pausing all calls untill refresh token finish issuing
   */
  isIssuingRefreshToken: boolean;

  /**
   * Callback for forcing logout from React state
   */
  private onLogout: (() => void) | null = null;

  /**
   * Creates the api.
   *
   * @param config The configuration to use.
   */
  constructor(config: ApiConfig = DEFAULT_API_CONFIG) {
    this.config = config;
    // construct the apisauce instance
    let apisauce = create({
      baseURL: this.config.url,
      timeout: this.config.timeout,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    apisauce.axiosInstance.interceptors.request.use(
      this.handleReqInterceptorSuccess
    );

    apisauce.axiosInstance.interceptors.response.use(
      this.handleResInterceptorSuccess,
      this.handleResInterceptorError
    );
    this.apisauce = apisauce;
    this.isIssuingRefreshToken = false;
    this.onLogout = null;
  }

  setLogoutCallback(callback: (() => void) | null) {
    this.onLogout = callback;
  }

  private forceLogout() {
    localStorageUtil.removeItem(localStoageKeys.TOKEN);
    localStorageUtil.removeItem(localStoageKeys.REFRESH_TOKEN);
    if (this.onLogout) {
      this.onLogout();
    }
  }

  /**
   * Gets token from localStorage and sets it in headers as Authorization
   * @param request The configuration to use.
   * @returns request with token.
   */
  handleReqInterceptorSuccess(request: any) {
    const token = localStorageUtil.getItem(localStoageKeys.TOKEN);
    if (token) {
      request.headers.Authorization = `Bearer ${token}`;
    }
    return request;
  }

  handleResInterceptorSuccess(response: any) {
    return response;
  }

  handleResInterceptorError = async (error: any) => {
    const originalRequest = error.config;

    if ([403].includes(error.response?.status)) {
      //   toast.error("Forbidden!");
      //   logout();
    }

    if (
      [401].includes(error.response?.status) &&
      originalRequest.url !== `/auth/refresh` &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      if (isEmpty(localStorageUtil.getItem(localStoageKeys.REFRESH_TOKEN))) {
        this.forceLogout();
      } else {
        if (!this.isIssuingRefreshToken) {
          const refreshed = await this.refreshAccessToken(originalRequest);
          if (refreshed) {
            return this.retryOriginalRequest(originalRequest);
          }
        } else {
          await this.waitForRefreshToken();
          if (localStorageUtil.getItem(localStoageKeys.TOKEN)) {
            return this.retryOriginalRequest(originalRequest);
          }
        }
      }
    }

    return Promise.reject(error);
  };

  retryOriginalRequest = (originalRequest: any): any => {
    return this.apisauce.axiosInstance.request(originalRequest);
  };

  // Function for pausing code until refresh token is resolved in another api instance
  async waitForRefreshToken() {
    if (!this.isIssuingRefreshToken) return;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await this.waitForRefreshToken();
  }

  async refreshAccessToken(request: any): Promise<boolean> {
    this.isIssuingRefreshToken = true;

    const refreshToken = localStorageUtil.getItem(
      localStoageKeys.REFRESH_TOKEN
    );

    const payload = {
      refresh_token: refreshToken,
    };

    try {
      const response: any = await this.apisauce.post("/auth/refresh", payload);

      const newToken = response.data?.auth?.access_token;
      const newRefreshToken = response.data?.auth?.refresh_token;

      if (response.status === 201 && newToken && newRefreshToken) {
        request.headers.Authorization = `Bearer ${newToken}`;
        localStorageUtil.setItem(localStoageKeys.TOKEN, newToken);
        localStorageUtil.setItem(
          localStoageKeys.REFRESH_TOKEN,
          newRefreshToken
        );
        return true;
      }

      this.forceLogout();
      return false;
    } catch (error) {
      this.forceLogout();
      return false;
    } finally {
      this.isIssuingRefreshToken = false;
    }
  }
}

export const api = new Api();
